// store/bankStatementsModel.ts
//@ts-nocheck
import { Action, action, Thunk, thunk } from "easy-peasy";
import { del, get } from "requests";
import { COLUMNS, Columns } from "../../pages/applications/modules/bankStatementColumns";
import {
  AccountColumns,
  BANK_ACCOUNT_COLUMNS,
} from "../../pages/applications/modules/bankStatementAccounts";

export interface BankAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  month: string;
  bankName: string;
}

export interface BankStatementsModel {
  columns: Columns[];
  bankStatements: any[];
  bankAccountColumns: AccountColumns[];
  bankAccounts: BankAccount[];
  loading: boolean;
  accountsLoading: boolean;
  error: string | null;
  setBankStatements: Action<BankStatementsModel, any[]>;
  setBankAccounts: Action<BankStatementsModel, BankAccount[]>;
  setLoading: Action<BankStatementsModel, boolean>;
  setAccountsLoading: Action<BankStatementsModel, boolean>;
  setError: Action<BankStatementsModel, string | null>;
  fetchBankStatements: Thunk<BankStatementsModel>;
  fetchBankAccounts: Thunk<BankStatementsModel>;
  deleteStatement: Thunk<
    BankStatementsModel,
    {
      statementId: number;
      appId: number;
      source: string;
      bankId?: number;
      setPlaidAccounts?: (updater: (prevAccounts: BankAccount[]) => BankAccount[]) => void;
      setPlaidTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setFlinksAccounts?: (updater: (prevAccounts: BankAccount[]) => BankAccount[]) => void;
      setFlinksTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setOcrAccounts?: (updater: (prevAccounts: any[]) => any[]) => void;
      setOcrTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setAlert?: (alert: { type: string; message: string }) => void;
      refreshFlinksData?: () => Promise<void>;
    }
  >;
}

const bankStatementsModel: BankStatementsModel = {
  columns: COLUMNS,
  bankAccountColumns: BANK_ACCOUNT_COLUMNS,
  bankStatements: [],
  bankAccounts: [],
  loading: false,
  accountsLoading: false,
  error: null,

  setBankStatements: action((state, bankStatements) => {
    state.bankStatements = bankStatements;
  }),

  setBankAccounts: action((state, bankAccounts) => {
    state.bankAccounts = bankAccounts;
  }),

  setLoading: action((state, loading) => {
    state.loading = loading;
  }),

  setAccountsLoading: action((state, accLoading) => {
    state.accountsLoading = accLoading;
  }),

  setError: action((state, error) => {
    state.error = error;
  }),

  filterBankAccounts: action((state, statementId) => {
    const prevAccounts = state.bankAccounts;
    state.bankAccounts = prevAccounts.filter((account) => account.statementid !== statementId);
  }),

  filterBankStatements: action((state, statementId) => {
    const prevBankStatements = state.bankStatements;
    state.bankStatements = prevBankStatements.filter(
      (statement) => statement.statementId !== statementId
    );
  }),

  fetchBankStatements: thunk(async (actions, id) => {
    actions.setLoading(true);
    actions.setError(null);
    try {
      const response = await get(`/moneythumb/transactions?applicationId=${id}`);

      let bankStatementsWithSource = [];

      if (response) {
        bankStatementsWithSource = response.map((transaction: any) => ({
          ...transaction,
          source: "MoneyThumb",
        }));
      }

      actions.setBankStatements(bankStatementsWithSource);
    } catch (error: any) {
      actions.setError("Error fetching bank statements: " + error.message);
    } finally {
      actions.setLoading(false);
    }
  }),

  fetchBankAccounts: thunk(async (actions, id) => {
    actions.setAccountsLoading(true);
    actions.setError(null);
    try {
      const response = await get(`/moneythumb/accounts/${id}`);
      actions.setBankAccounts(response);
    } catch (error: any) {
      actions.setError("Error fetching bank accounts: " + error.message);
    } finally {
      actions.setAccountsLoading(false);
    }
  }),

  deleteStatement: thunk<
    BankStatementsModel,
    {
      statementId: number;
      appId: number;
      source: string;
      bankId?: number;
      setPlaidAccounts?: (updater: (prevAccounts: BankAccount[]) => BankAccount[]) => void;
      setPlaidTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setFlinksAccounts?: (updater: (prevAccounts: BankAccount[]) => BankAccount[]) => void;
      setFlinksTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setOcrAccounts?: (updater: (prevAccounts: any[]) => any[]) => void;
      setOcrTransactions?: (updater: (prevTransactions: any[]) => any[]) => void;
      setAlert?: (alert: { type: string; message: string }) => void;
      refreshFlinksData?: () => Promise<void>;
    }
  >(
    async (
      actions,
      {
        statementId,
        appId,
        source,
        bankId,
        setPlaidAccounts,
        setPlaidTransactions,
        setFlinksAccounts,
        setFlinksTransactions,
        setOcrAccounts,
        setOcrTransactions,
        setAlert,
        refreshFlinksData,
      }
    ) => {
      actions.setLoading(true);
      actions.setError(null);
      try {
        const requestData = { statementId, appId };
        let response;

        if (source && source === "Plaid") {
          requestData.source = source;
          response = await del(`/plaidService/deleteStatement`, { data: requestData });
          try {
            await del(`/bankStatements/plaidBankSummary/delete`, { data: requestData });
            console.log("Plaid Bank Summary Statement deleted successfully in Underwriting Tab");
          } catch (e) {
            console.error("Error deleting financial report of Plaid Account:", e);
            setAlert?.({
              type: "warning",
              message:
                "Statement deleted but financial report Bank Summary Statement failed for Underwriting",
            });
          }
        } else if (source && source === "Flinks") {
          requestData.source = source;
          requestData.bankId = bankId;
          response = await del(`/flinksService/deleteStatement`, { data: requestData });

          if (response && response.status === "success") {
            if (refreshFlinksData) {
              console.log("Refreshing Flinks banks from server...");
              await refreshFlinksData();
            } else {
              // Fallback: old manual filtering logic
              if (setFlinksAccounts) {
                setFlinksAccounts((prevAccounts) =>
                  prevAccounts.filter((account) => {
                    const accountIdFromState = account.accountId.includes("::")
                      ? account.accountId.split("::")[1]
                      : account.accountId;

                    const statementIdToCheck = String(statementId).includes("::")
                      ? String(statementId).split("::")[1]
                      : String(statementId);

                    return accountIdFromState !== statementIdToCheck;
                  })
                );
              }
              if (setFlinksTransactions) {
                setFlinksTransactions((prevTransactions) =>
                  prevTransactions.filter((transaction) => {
                    const txAccountId = transaction.transactionAccountId;
                    const statementIdToCheck = String(statementId).includes("::")
                      ? String(statementId).split("::")[1]
                      : String(statementId);

                    return txAccountId !== statementIdToCheck;
                  })
                );
              }
            }
          }

          setAlert({ type: "success", message: "Successfully deleted Flinks statement!" });
          return;
        } else if (source && source === "OCR") {
          response = await del(`/ocr/statements/${appId}/${statementId}`);
          if (response && response.status === "success") {
            if (setOcrAccounts) {
              setOcrAccounts((prevAccounts) => {
                return prevAccounts.filter((account) => account.statementid !== statementId);
              });
            }
            if (setOcrTransactions) {
              setOcrTransactions((prevTransactions) => {
                return prevTransactions.filter(
                  (transaction) => transaction.statementId !== statementId
                );
              });
            }
          }
          setAlert({ type: "success", message: "Successfully deleted OCR statement!" });
          return;
        } else {
          response = await del(`/moneythumb/deleteStatement`, { data: requestData });
        }

        if (
          !response ||
          (!response.success && !(response.status && response.status === "success"))
        ) {
          throw new Error("Error deleting the statement");
        } else {
          if (source && source === "Plaid") {
            if (setPlaidAccounts) {
              setPlaidAccounts((prevAccounts) =>
                prevAccounts.filter((account) => account.accountId !== statementId)
              );
            }
            if (setPlaidTransactions) {
              setPlaidTransactions((prevTransactions) =>
                prevTransactions.filter((transaction) => transaction.id !== statementId)
              );
            }
          } else {
            actions.filterBankStatements(statementId);
            actions.filterBankAccounts(statementId);
          }

          setAlert({ type: "success", message: "Successfully deleted bank statement!" });
        }
      } catch (error: any) {
        console.error(error);
        actions.setError("Error deleting bank statement: " + error.message);
        setAlert({ type: "error", message: "Error deleting bank statement " });
      } finally {
        actions.setLoading(false);
      }
    }
  ),
};

export default bankStatementsModel;
